"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, } from "react";
import { IconV2 } from "../../../icon_v2";
export const CardSlider = ({ children, className = "", showIndicators = true, infiniteLoop = false, autoPlay = false, autoPlayInterval = 5000, pauseOnHover = true, transitionEffect = "slide", transitionDuration = 300, itemsPerView = { mobile: 1, sm: 2, md: 3, lg: 4 }, 
// Card sizing options with defaults
cardSizing = "responsive", cardWidth = 320, cardGap = 16, // Gap between cards in pixels
minCardWidth = 200, // Minimum width for a card in responsive mode
maxCardsToShow = 6, // Maximum number of cards to show regardless of space
prevArrow, nextArrow, renderArrow, 
// Arrow positioning
arrowPosition = "inside", arrowOffset = 16, debug = false, }) => {
    var _a;
    // Safe handling of children
    const childrenArray = React.Children.toArray(children !== null && children !== void 0 ? children : []);
    // State for slider
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleItems, setVisibleItems] = useState((_a = itemsPerView.lg) !== null && _a !== void 0 ? _a : 4);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isPaused, setIsPaused] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const sliderRef = useRef(null);
    const contentRef = useRef(null);
    const autoPlayTimerRef = useRef(null);
    // Calculate max index safely
    const maxIndex = Math.max(0, childrenArray.length - visibleItems);
    // Calculate how many cards can fit in the container
    const calculateVisibleItems = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!sliderRef.current)
            return;
        const _containerWidth = sliderRef.current.clientWidth - 32; // Account for padding
        setContainerWidth(_containerWidth);
        let newVisibleItems;
        // Different calculation based on sizing strategy
        switch (cardSizing) {
            case "fixed":
                // Fixed width cards - calculate how many fit
                newVisibleItems = Math.floor(containerWidth / (cardWidth + cardGap));
                break;
            case "fluid":
                // Fluid width - use the itemsPerView settings directly based on screen size
                if (window.innerWidth < 640) {
                    newVisibleItems = (_a = itemsPerView.mobile) !== null && _a !== void 0 ? _a : 1;
                }
                else if (window.innerWidth < 768) {
                    newVisibleItems = (_b = itemsPerView.sm) !== null && _b !== void 0 ? _b : 2;
                }
                else if (window.innerWidth < 1024) {
                    newVisibleItems = (_c = itemsPerView.md) !== null && _c !== void 0 ? _c : 3;
                }
                else {
                    newVisibleItems = (_d = itemsPerView.lg) !== null && _d !== void 0 ? _d : 4;
                }
                break;
            case "responsive":
            default:
                // Responsive - calculate based on minimum card width
                newVisibleItems = Math.floor(containerWidth / (minCardWidth + cardGap));
                // Apply responsive constraints
                if (window.innerWidth < 640) {
                    newVisibleItems = Math.min(newVisibleItems, (_e = itemsPerView.mobile) !== null && _e !== void 0 ? _e : 1);
                }
                else if (window.innerWidth < 768) {
                    newVisibleItems = Math.min(newVisibleItems, (_f = itemsPerView.sm) !== null && _f !== void 0 ? _f : 2);
                }
                else if (window.innerWidth < 1024) {
                    newVisibleItems = Math.min(newVisibleItems, (_g = itemsPerView.md) !== null && _g !== void 0 ? _g : 3);
                }
                else {
                    newVisibleItems = Math.min(newVisibleItems, (_h = itemsPerView.lg) !== null && _h !== void 0 ? _h : 4);
                }
                break;
        }
        // Ensure we have at least 1 visible item and respect the maximum
        newVisibleItems = Math.max(1, Math.min(newVisibleItems, maxCardsToShow));
        // Don't show more cards than we have
        newVisibleItems = Math.min(newVisibleItems, childrenArray.length);
        setVisibleItems(newVisibleItems);
    }, [
        cardSizing,
        cardWidth,
        cardGap,
        minCardWidth,
        itemsPerView,
        maxCardsToShow,
        childrenArray.length,
    ]);
    // Navigation handlers with infinite loop support
    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
        else if (infiniteLoop) {
            // If at the beginning and infinite loop is enabled, go to the end
            setCurrentIndex(maxIndex);
        }
    }, [currentIndex, infiniteLoop, maxIndex]);
    const handleNext = useCallback(() => {
        if (currentIndex < maxIndex) {
            setCurrentIndex(currentIndex + 1);
        }
        else if (infiniteLoop) {
            // If at the end and infinite loop is enabled, go back to the beginning
            setCurrentIndex(0);
        }
    }, [currentIndex, infiniteLoop, maxIndex]);
    // Auto-play functionality
    const startAutoPlay = useCallback(() => {
        if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
        }
        autoPlayTimerRef.current = setInterval(() => {
            if (!isPaused) {
                handleNext();
            }
        }, autoPlayInterval);
    }, [autoPlayInterval, handleNext, isPaused]);
    const stopAutoPlay = useCallback(() => {
        if (autoPlayTimerRef.current) {
            clearInterval(autoPlayTimerRef.current);
            autoPlayTimerRef.current = null;
        }
    }, []);
    const toggleAutoPlay = () => {
        setIsPlaying(!isPlaying);
    };
    // Handle auto-play
    useEffect(() => {
        if (isPlaying) {
            startAutoPlay();
        }
        else {
            stopAutoPlay();
        }
        return () => {
            stopAutoPlay();
        };
    }, [isPlaying, startAutoPlay, stopAutoPlay]);
    // Handle responsive behavior
    useEffect(() => {
        const handleResize = () => {
            calculateVisibleItems();
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [calculateVisibleItems]);
    // Reset current index if it's out of bounds after a resize
    useEffect(() => {
        if (currentIndex > maxIndex) {
            setCurrentIndex(maxIndex);
        }
    }, [currentIndex, maxIndex]);
    // Touch handlers for mobile swipe
    const handleTouchStart = (e) => {
        var _a;
        setTouchStart((_a = e.targetTouches[0]) === null || _a === void 0 ? void 0 : _a.clientX);
        if (pauseOnHover && isPlaying) {
            setIsPaused(true);
        }
    };
    const handleTouchMove = (e) => {
        var _a;
        setTouchEnd((_a = e.targetTouches[0]) === null || _a === void 0 ? void 0 : _a.clientX);
    };
    const handleTouchEnd = () => {
        if (touchStart - touchEnd > 75) {
            // Swipe left
            handleNext();
        }
        if (touchStart - touchEnd < -75) {
            // Swipe right
            handlePrev();
        }
        if (pauseOnHover && isPlaying) {
            setIsPaused(false);
        }
    };
    // Mouse enter/leave handlers for pause on hover
    const handleMouseEnter = () => {
        if (pauseOnHover && isPlaying) {
            setIsPaused(true);
        }
    };
    const handleMouseLeave = () => {
        if (pauseOnHover && isPlaying) {
            setIsPaused(false);
        }
    };
    // Keyboard navigation
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (document.activeElement === sliderRef.current ||
                (sliderRef.current &&
                    sliderRef.current.contains(document.activeElement))) {
                if (e.key === "ArrowLeft") {
                    handlePrev();
                }
                else if (e.key === "ArrowRight") {
                    handleNext();
                }
                else if (e.key === " " || e.key === "Spacebar") {
                    // Toggle play/pause with spacebar
                    e.preventDefault();
                    toggleAutoPlay();
                }
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleNext, handlePrev]);
    // Check if we should show navigation
    const shouldShowNavigation = childrenArray.length > visibleItems;
    // Determine button states based on position and infinite loop setting
    const isPrevButtonActive = infiniteLoop || currentIndex > 0;
    const isNextButtonActive = infiniteLoop || currentIndex < maxIndex;
    // If no children, render empty state
    if (childrenArray.length === 0) {
        return (_jsx("div", { className: `w-full rounded-lg border border-gray-200 bg-gray-50 p-8 text-center ${className}`, children: _jsx("p", { className: "text-gray-500", children: "No items to display." }) }));
    }
    // Calculate card width based on sizing strategy
    const getCardWidth = () => {
        switch (cardSizing) {
            case "fixed":
                return `${cardWidth}px`;
            case "fluid":
                return `${100 / visibleItems}%`;
            case "responsive":
            default:
                // For responsive, we calculate a percentage but with a minimum width
                return `calc(${100 / visibleItems}% - ${cardGap}px)`;
        }
    };
    // Generate transition styles based on selected effect
    const getTransitionStyles = () => {
        const baseStyles = {
            transitionDuration: `${transitionDuration}ms`,
        };
        switch (transitionEffect) {
            case "fade":
                return Object.assign(Object.assign({}, baseStyles), { transition: `opacity ${transitionDuration}ms ease-in-out` });
            case "zoom":
                return Object.assign(Object.assign({}, baseStyles), { transition: `transform ${transitionDuration}ms ease-in-out, opacity ${transitionDuration}ms ease-in-out` });
            case "slide":
            default:
                return Object.assign(Object.assign({}, baseStyles), { transform: `translateX(-${currentIndex * (100 / visibleItems)}%)`, transition: `transform ${transitionDuration}ms ease-in-out` });
        }
    };
    // Generate item styles based on transition effect and sizing strategy
    const getItemStyles = (index) => {
        const isVisible = index >= currentIndex && index < currentIndex + visibleItems;
        const width = getCardWidth();
        const baseStyles = {
            width,
            marginRight: `${cardGap}px`,
        };
        switch (transitionEffect) {
            case "fade":
                return Object.assign(Object.assign({}, baseStyles), { opacity: isVisible ? 1 : 0, transition: `opacity ${transitionDuration}ms ease-in-out` });
            case "zoom":
                return Object.assign(Object.assign({}, baseStyles), { opacity: isVisible ? 1 : 0, transform: isVisible ? "scale(1)" : "scale(0.8)", transition: `transform ${transitionDuration}ms ease-in-out, opacity ${transitionDuration}ms ease-in-out` });
            case "slide":
            default:
                return baseStyles;
        }
    };
    // Get arrow positioning styles based on arrowPosition prop
    const getArrowPositionStyles = (type) => {
        const isLeft = type === "prev";
        const baseStyles = {
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
        };
        switch (arrowPosition) {
            case "outside":
                // Position arrows outside the slider content
                return Object.assign(Object.assign({}, baseStyles), { [isLeft ? "left" : "right"]: `-${arrowOffset + 40}px` });
            case "edge":
                // Position arrows at the edge of the slider
                return Object.assign(Object.assign({}, baseStyles), { [isLeft ? "left" : "right"]: `${arrowOffset}px` });
            case "inside":
            default:
                // Position arrows inside the slider content with padding
                return Object.assign(Object.assign({}, baseStyles), { [isLeft ? "left" : "right"]: `${arrowOffset}px` });
        }
    };
    // Simplified arrow positioning for better visibility
    // const getLeftArrowStyles = (): CSSProperties => {
    //   switch (arrowPosition) {
    //     case "outside":
    //       return { left: `-${arrowOffset + 40}px` };
    //     case "edge":
    //       return { left: `${arrowOffset}px` };
    //     case "inside":
    //     default:
    //       return { left: `${arrowOffset}px` };
    //   }
    // };
    // const getRightArrowStyles = (): CSSProperties => {
    //   switch (arrowPosition) {
    //     case "outside":
    //       return { right: `-${arrowOffset + 40}px` };
    //     case "edge":
    //       return { right: `${arrowOffset}px` };
    //     case "inside":
    //     default:
    //       return { right: `${arrowOffset}px` };
    //   }
    // };
    return (_jsxs("div", { className: `relative w-full ${className}`, ref: sliderRef, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: 0, "aria-roledescription": "carousel", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsxs("div", { className: "relative overflow-hidden", onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, children: [_jsx("div", { className: "px-4 sm:px-5 md:px-6", ref: contentRef, children: _jsx("div", { className: "flex", style: getTransitionStyles(), children: childrenArray.map((child, index) => (_jsx("div", { className: "flex-shrink-0", style: getItemStyles(index), "aria-roledescription": "slide", children: _jsx("div", { className: "h-full p-2", children: child }) }, index))) }) }), shouldShowNavigation && (_jsxs(_Fragment, { children: [renderArrow ? (
                            // Use the renderArrow function if provided
                            _jsx("div", { className: "absolute", style: getArrowPositionStyles("prev"), children: renderArrow({
                                    type: "prev",
                                    onClick: handlePrev,
                                    disabled: !isPrevButtonActive,
                                    className: isPrevButtonActive
                                        ? "cursor-pointer opacity-90 hover:opacity-100"
                                        : "cursor-not-allowed opacity-50",
                                }) })) : prevArrow !== null ? (
                            // Use the prevArrow component if provided
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                            _jsx("div", { className: "absolute", style: getArrowPositionStyles("prev"), onClick: isPrevButtonActive ? handlePrev : undefined, "aria-disabled": !isPrevButtonActive, children: _jsx("div", { style: {
                                        cursor: isPrevButtonActive ? "pointer" : "not-allowed",
                                        opacity: isPrevButtonActive ? 0.9 : 0.5,
                                    }, children: prevArrow }) })) : (
                            // Use the default button if no custom component is provided
                            _jsx("button", { className: `absolute flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gray-200 sm:h-12 sm:w-12 ${isPrevButtonActive
                                    ? "cursor-pointer text-gray-800 opacity-90 hover:opacity-100"
                                    : "cursor-not-allowed text-gray-300 opacity-50"}`, style: getArrowPositionStyles("prev"), onClick: handlePrev, disabled: !isPrevButtonActive, "aria-label": "Previous slide", children: _jsx(IconV2, { type: "fa", icon: {
                                        icon: "chevron-left",
                                        fixedWidth: true,
                                    } }) })), renderArrow ? (
                            // Use the renderArrow function if provided
                            _jsx("div", { className: "absolute", style: getArrowPositionStyles("next"), children: renderArrow({
                                    type: "next",
                                    onClick: handleNext,
                                    disabled: !isNextButtonActive,
                                    className: isNextButtonActive
                                        ? "cursor-pointer opacity-90 hover:opacity-100"
                                        : "cursor-not-allowed opacity-50",
                                }) })) : nextArrow !== null ? (
                            // Use the nextArrow component if provided
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                            _jsx("div", { className: "absolute", style: getArrowPositionStyles("next"), onClick: isNextButtonActive ? handleNext : undefined, "aria-disabled": !isNextButtonActive, children: _jsx("div", { style: {
                                        cursor: isNextButtonActive ? "pointer" : "not-allowed",
                                        opacity: isNextButtonActive ? 0.9 : 0.5,
                                    }, children: nextArrow }) })) : (
                            // Use the default button if no custom component is provided
                            _jsx("button", { className: `absolute flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-gray-200 sm:h-12 sm:w-12 ${isNextButtonActive
                                    ? "cursor-pointer text-gray-800 opacity-90 hover:opacity-100"
                                    : "cursor-not-allowed text-gray-300 opacity-50"}`, style: getArrowPositionStyles("next"), onClick: handleNext, disabled: !isNextButtonActive, "aria-label": "Next slide", children: _jsx(IconV2, { type: "fa", icon: {
                                        icon: "chevron-right",
                                        fixedWidth: true,
                                    } }) }))] }))] }), autoPlay && autoPlayInterval && (_jsx("button", { className: "absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 shadow-md transition-all hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-200", onClick: toggleAutoPlay, "aria-label": isPlaying ? "Pause auto-play" : "Start auto-play", children: isPlaying ? (_jsx(IconV2, { type: "fa", icon: {
                        icon: "pause",
                        fixedWidth: true,
                    } })) : (_jsx(IconV2, { type: "fa", icon: {
                        icon: "play",
                        fixedWidth: true,
                    } })) })), showIndicators && shouldShowNavigation && (_jsx("div", { className: "absolute bottom-0 left-0 right-0 flex justify-center pb-2", children: _jsx("div", { className: "flex space-x-1.5", children: Array.from({ length: maxIndex + 1 }).map((_, index) => (_jsx("button", { className: `h-1.5 rounded-full transition-all ${index === currentIndex
                            ? "w-6 bg-gray-800"
                            : "w-1.5 bg-gray-300"}`, onClick: () => setCurrentIndex(index), "aria-label": `Go to slide ${index + 1}`, "aria-current": index === currentIndex ? "true" : "false" }, index))) }) }))] }));
};
